import * as Api from '@/api';
import { Badge } from '@/models/packet';
import { defineModule, localActionContext } from 'direct-vuex';
import { WebRequestStatus } from '@/models';
import * as Utils from '@/utils';

interface State {
  activeRequests: {
    // Initial load is tracked by objectiveId
    initialLoad: Utils.RequestTrackerById;

    // Update is tracked by badgeId
    update: Utils.RequestTrackerById;
  };
  badges: {
    [key: number]: Badge[];
  };
}

const getInitialState = (): State => {
  return {
    activeRequests: {
      initialLoad: new Utils.RequestTrackerById({}),
      update: new Utils.RequestTrackerById({})
    },

    badges: {}
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const badgesActionContext = (context: any) =>
  // eslint-disable-next-line  @typescript-eslint/no-use-before-define
  localActionContext(context, badges);

const badges = defineModule({
  namespaced: true as true,
  state: getInitialState(),
  getters: {
    loadStatus: state => (objectiveId: number) =>
      state.activeRequests.initialLoad.getStatus(objectiveId) ||
      WebRequestStatus.READY,
    updateStatus: state => (badgeId: number) =>
      state.activeRequests.update.getStatus(badgeId) || WebRequestStatus.READY
  },
  mutations: {
    setBadges: (
      state,
      { objectiveId, badges }: { objectiveId: number; badges: Badge[] }
    ) => {
      state.badges = {
        ...state.badges,
        [objectiveId]: badges
      };
    },

    updateBadge: (state, badge: Badge) => {
      state.badges = {
        ...state.badges,
        [badge.objectiveId]: Utils.updateFirstWhere(
          state.badges[badge.objectiveId],
          b => b.badgeId === badge.badgeId,
          () => badge
        )
      };
    },

    updateLoadStatus: (
      state,
      { objectiveId, status }: { objectiveId: number; status: WebRequestStatus }
    ) => {
      state.activeRequests = {
        ...state.activeRequests,
        initialLoad: state.activeRequests.initialLoad.setStatus(
          objectiveId,
          status
        )
      };
    },

    updateUpdateStatus: (
      state,
      { badgeId, status }: { badgeId: number; status: WebRequestStatus }
    ) => {
      state.activeRequests = {
        ...state.activeRequests,
        update: state.activeRequests.update.setStatus(badgeId, status)
      };
    },

    removeBadgesForOutcome: (state, outcomeId: number) => {
      const updated = Object.values(state.badges)
        .flatMap(badges => badges)
        .filter(badge => badge.outcomeId !== outcomeId)
        .reduce(
          (acc: { [key: number]: Badge[] }, badge) => ({
            ...acc,
            [badge.objectiveId]: (acc[badge.objectiveId] || []).concat([badge])
          }),
          {}
        );

      state.badges = updated;
    },

    removeBadgesForObjective: (state, objectiveId: number) => {
      const updated = Object.values(state.badges)
        .flatMap(badges => badges)
        .filter(badge => badge.objectiveId !== objectiveId)
        .reduce(
          (acc: { [key: number]: Badge[] }, badge) => ({
            ...acc,
            [badge.objectiveId]: (acc[badge.objectiveId] || []).concat([badge])
          }),
          {}
        );

      state.badges = updated;
    }
  },
  actions: {
    async fetchBadges(context, objectiveId: number) {
      const { commit } = badgesActionContext(context);

      commit.updateLoadStatus({
        objectiveId,
        status: WebRequestStatus.IN_PROGRESS
      });

      try {
        const badges = await Api.secure.getBadges(objectiveId);

        commit.updateLoadStatus({
          objectiveId,
          status: WebRequestStatus.SUCCESS
        });

        commit.setBadges({ objectiveId, badges });
      } catch (error) {
        console.error(error);

        commit.updateLoadStatus({
          objectiveId,
          status: WebRequestStatus.FAILURE
        });

        await Utils.sleep(3000);
      }

      commit.updateLoadStatus({
        objectiveId,
        status: WebRequestStatus.READY
      });
    },
    async updateBadge(context, badge: Badge) {
      const { commit } = badgesActionContext(context);

      commit.updateUpdateStatus({
        badgeId: badge.badgeId,
        status: WebRequestStatus.IN_PROGRESS
      });

      try {
        const updated = await Api.secure.updateBadge(badge);

        commit.updateBadge(updated);

        commit.updateUpdateStatus({
          badgeId: badge.badgeId,
          status: WebRequestStatus.SUCCESS
        });

        await Utils.sleep(1000);
      } catch (error) {
        console.error(error);

        commit.updateUpdateStatus({
          badgeId: badge.badgeId,
          status: WebRequestStatus.FAILURE
        });

        await Utils.sleep(4000);
      }

      commit.updateUpdateStatus({
        badgeId: badge.badgeId,
        status: WebRequestStatus.READY
      });
    }
  }
});

export default badges;
